import {
  Button,
  Column,
  FlexGrid as Grid,
  InlineLoading,
  InlineNotification,
  Row,
  Tile,
} from '@carbon/react';
import { ChevronLeft, ChevronRight } from '@carbon/react/icons';
import { EvaluationCount } from 'components/evaluations/evaluation-count';
import { EvaluationList } from 'components/evaluations/evaluation-list';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { PackageEvaluationsResponse } from 'types/evaluation-api/responses';
import {
  createShowPackageEvalDraftAtom,
  usePackageEvaluationsAtom,
} from './atoms';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { EvaluationFormVersionSwitcher } from './evaluation-form-version-switcher';
import {
  EvaluationVersionSwitcher,
  EvaluationVersion,
} from './evaluation-result/evaluation-version-switcher';
import { EvalNavLayout } from './eval-3-workspace/eval-nav-layout';

import style from './packages-evaluation.module.scss';

const EvaluationsImpl = ({
  evaluations,
  getEvaluations,
  href,
}: {
  evaluations: PackageEvaluationsResponse[];
  getEvaluations: () => Promise<void>;
  href: string;
}) => {
  const location = useLocation();
  const evalCount = evaluations ? evaluations.length : 0;
  const showDratInitialValue =
    evalCount === 0 || location.pathname.includes('/new');
  const showPackageEvalDraftAtom = useMemo(
    () => createShowPackageEvalDraftAtom(showDratInitialValue),
    [showDratInitialValue]
  );
  const [showDraft, setShowDraft] = useAtom(showPackageEvalDraftAtom);
  const { path } = useRouteMatch();
  const history = useHistory();

  const { id } = useParams<{ id: string }>();
  const packageId = parseInt(id, 10);

  // URL is the source of truth for which eval version is shown:
  //   • Eval 2.0 covers `/packages/:id/evaluations*` (existing routes —
  //     `/evaluations/:evalId`, `/evaluations/new`, `/evaluations/combinations`).
  //   • LEGACY Eval 3.0 covers `/packages/:id/eval3-legacy` and `/eval3-legacy/:calcId`
  //     (INS-11484). The NEW Eval 3.0 page owns `/eval3*` and renders via
  //     `Eval3NewPage` in the router — never here.
  //   • Any other package subpath reaching this legacy shell defaults to 3.0.
  const eval2Match = useRouteMatch({
    path: `${path}/evaluations`,
  });
  const eval3Match = useRouteMatch({
    path: `${path}/eval3-legacy`,
  });
  const eval3RunMatch = useRouteMatch<{ calcId: string }>({
    path: `${path}/eval3-legacy/:calcId`,
    exact: true,
  });
  const evaluationVersion: EvaluationVersion = eval2Match ? '2.0' : '3.0';
  const initialCalcId = useMemo(() => {
    if (!eval3RunMatch?.params.calcId) return undefined;
    const parsed = parseInt(eval3RunMatch.params.calcId, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [eval3RunMatch?.params.calcId]);

  const handleEval3CalcSelect = useCallback(
    (calcId: number) => {
      history.replace(`/packages/${packageId}/eval3-legacy/${calcId}`);
    },
    [history, packageId]
  );

  const [isEvaluationListVisible, setEvaluationListVisible] = useState(false);
  const handleClick = () => {
    setEvaluationListVisible((current) => !current);
  };

  return (
    <Grid className="bx--no-gutter" condensed fullWidth>
      {evaluationVersion === '3.0' ? (
        // Fallback for an unrecognized package subpath that reaches this legacy
        // shell (bare `/packages/:id` is handled upstream by the router's eval3
        // bounce). Land on the legacy Eval 3.0 page; EvalNavLayout auto-picks a calc.
        !eval3Match ? (
          <Redirect to={`/packages/${packageId}/eval3-legacy`} />
        ) : (
          <EvalNavLayout
            packageId={packageId}
            initialCalcId={initialCalcId}
            onCalcSelect={handleEval3CalcSelect}
          />
        )
      ) : (
        <>
          <h2 className={style.subtitleEval}>Package Evaluation</h2>
          <Row>
            <Column
              md={isEvaluationListVisible ? 0 : 13}
              lg={isEvaluationListVisible ? 0 : 3}
              className={style.scrollRelativeColumn}
            >
              {evaluations && <EvaluationCount count={evaluations.length} />}
              <EvaluationList
                evaluations={evaluations}
                showDraft={showDraft}
                onDraftClick={() => {
                  setShowDraft(true);
                }}
                onEvaluationClick={() => setShowDraft(false)}
                draftLabel="New"
              />
            </Column>
            <Column
              md={isEvaluationListVisible ? 16 : 12}
              lg={isEvaluationListVisible ? 16 : 13}
              className={style.contentEvaluation}
            >
              {evaluations && evaluations.length ? (
                <div className={style.closeAreabtn}>
                  <Button
                    renderIcon={
                      isEvaluationListVisible ? ChevronRight : ChevronLeft
                    }
                    kind="ghost"
                    hasIconOnly
                    iconDescription={
                      isEvaluationListVisible
                        ? `Show Evaluation List`
                        : `Hide Evaluation List`
                    }
                    tooltipAlignment="start"
                    tooltipPosition="top"
                    size="sm"
                    className={style.closePanelBtn}
                    onClick={handleClick}
                  />
                </div>
              ) : null}
              <Tile className={style.contentEvaluation}>
                <Switch>
                  {/* Bare package (`${path}/`) + the Eval 2 switcher entry
                      (`${path}/evaluations`) both land on the first evaluation (or the
                      new-eval form when none exist) — INS-11484. */}
                  <Route exact path={[`${path}/`, `${path}/evaluations`]}>
                    {evaluations ? (
                      evaluations.length ? (
                        <Redirect to={evaluations[0].href} />
                      ) : (
                        <EvaluationFormVersionSwitcher
                          href={href}
                          getEvaluations={getEvaluations}
                        />
                      )
                    ) : (
                      <InlineLoading />
                    )}
                  </Route>
                  <Route exact path={`${path}/evaluations/new`}>
                    <EvaluationFormVersionSwitcher
                      href={href}
                      getEvaluations={getEvaluations}
                    />
                  </Route>
                  <Route exact path={`${path}/evaluations/combinations`}>
                    {showDraft ? (
                      <EvaluationFormVersionSwitcher
                        href={href}
                        getEvaluations={getEvaluations}
                      />
                    ) : (
                      <EvaluationVersionSwitcher isCombined />
                    )}
                  </Route>
                  <Route exact path={`${path}/evaluations/:evalId`}>
                    {showDraft ? (
                      <EvaluationFormVersionSwitcher
                        href={href}
                        getEvaluations={getEvaluations}
                      />
                    ) : (
                      <EvaluationVersionSwitcher />
                    )}
                  </Route>
                </Switch>
              </Tile>
            </Column>
          </Row>
        </>
      )}
    </Grid>
  );
};

const Evaluations = ({ href }: { href: string }) => {
  const evaluationsAtom = usePackageEvaluationsAtom(href);
  const [evaluations, getEvaluations] = useAtom(evaluationsAtom);

  if (evaluations.data && 'error' in evaluations.data)
    return (
      <InlineNotification
        kind="error"
        title={evaluations.data.error}
        lowContrast
      />
    );

  return (
    <EvaluationsImpl
      href={href}
      evaluations={evaluations.data || []}
      getEvaluations={getEvaluations}
    />
  );
};

export { Evaluations };
